import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import { reactRenderingRoute } from 'utils/generic/route-manipulation';
import { ICasinoCachedGames } from 'interfaces/casino-data';
import { useMetaData } from 'hooks/useMetaData';
import { getActiveGames } from 'store/selectors/new-casino';

export const useCsMetaUpdates = (): void => {
  const { t } = useTranslation();
  const activeGames = useSelector(getActiveGames) as ICasinoCachedGames;
  const { generateMetaData, oldMetaData } = useMetaData();

  const ogUrlBase = useRef<string>();
  const initialDocumentTitle = useRef<string>(
    GLOBALS.currentPage.seoParams?.pageTitle || document.title
  );

  useEffect(() => {
    if (!ogUrlBase.current) {
      ogUrlBase.current = (
        document.querySelector('meta[property="og:url"]') as HTMLMetaElement
      )?.content;
    }

    return () => {
      const ogTitleMeta = document.querySelector('meta[property="og:title"]');
      const twitterTitleMeta = document.querySelector(
        'meta[name="twitter:title"]'
      );

      const ogDescriptionMeta = document.querySelector(
        'meta[property="og:description"]'
      );

      const descriptionMeta = document.querySelector(
        'meta[name="description"]'
      );

      const twitterDescriptionMeta = document.querySelector(
        'meta[name="twitter:description"]'
      );

      const itemPropDescriptionMeta = document.querySelector(
        'meta[itemprop="description"]'
      );

      const ogUrlMeta = document.querySelector('meta[property="og:url"]');
      const canonicalLink = document.querySelector('link[rel="canonical"]');

      document.title = initialDocumentTitle.current;
      ogTitleMeta?.setAttribute('content', GLOBALS.currentPage.title || '');
      twitterTitleMeta?.setAttribute(
        'content',
        GLOBALS.currentPage.title || ''
      );
      ogUrlMeta?.setAttribute('content', ogUrlBase.current || '');
      canonicalLink?.setAttribute('href', ogUrlBase.current || '');
      ogDescriptionMeta?.setAttribute(
        'content',
        oldMetaData.ogDescriptionMeta || ''
      );
      descriptionMeta?.setAttribute(
        'content',
        oldMetaData.descriptionMeta || ''
      );
      twitterDescriptionMeta?.setAttribute(
        'content',
        oldMetaData.twitterDescriptionMeta || ''
      );
      itemPropDescriptionMeta?.setAttribute(
        'content',
        oldMetaData.itemPropDescriptionMeta || ''
      );
    };
  }, []);

  useEffect(() => {
    if (SpringConfigs.MOCKED_DATA) {
      return;
    }

    const reactRoute = reactRenderingRoute(window.location.pathname);
    const siteNameSeparator =
      GLOBALS.appConfigs.siteNameSeparatorInTitles || ' - ';

    let completeUrl;
    let skipCanonicalAndOgUrl = false;

    if (ogUrlBase.current?.endsWith(reactRoute)) {
      completeUrl = ogUrlBase.current;
      skipCanonicalAndOgUrl = true;
    } else {
      completeUrl =
        (ogUrlBase.current || window.location.origin) +
        reactRenderingRoute(window.location.pathname);
    }

    const numberOfActiveGames = Object.keys(activeGames).length;
    let metaTitle = '';
    let metaDescription = '';
    const activeGamesArray = Object.values(activeGames);
    const firstGame = activeGamesArray[0];

    if (numberOfActiveGames === 0) {
      return;
    }

    if (numberOfActiveGames === 1) {
      metaTitle = t('casino.metaTitle')
        .replace('{gameName}', firstGame.data.name || '')
        .replace('{providerName}', firstGame.data.provider_title || '')
        .replace('{siteNameSeparator}', siteNameSeparator)
        .replace('{pageTitle}', GLOBALS.currentPage.title || '');

      metaDescription = t('casino.metaDescription')
        .replace('{gameDescripiton}', firstGame.data.description || '')
        .replaceAll('{siteNameSeparator}', siteNameSeparator)
        .replaceAll('{siteName}', GLOBALS.appConfigs.siteName || '')
        .replace('{pageSeoDescription}', GLOBALS.currentPage.title || '');
    } else {
      const providerTitlesSet = new Set();
      const secondGame = activeGamesArray[1];
      Object.values(activeGames).forEach(activeGame =>
        providerTitlesSet.add(activeGame.data.provider_title)
      );

      if (providerTitlesSet.size === 1) {
        metaTitle = t('casino.metaTitleMultiviewSameProvider')
          .replace('{gameNameMain}', firstGame.data.name)
          .replace('{gameNameSecondary}', secondGame.data.name)
          .replace('{providerNameMain}', firstGame.data.provider_title)
          .replace('{siteNameSeparator}', siteNameSeparator)
          .replace('{pageTitle}', GLOBALS.currentPage.title || '');
      } else {
        metaTitle = t('casino.metaTitleMultiview')
          .replace('{gameNameMain}', firstGame.data.name)
          .replace('{gameNameSecondary}', secondGame.data.name)
          .replace('{providerNameMain}', firstGame.data.provider_title)
          .replace('{providerNameSecondary}', secondGame.data.provider_title)
          .replace('{siteNameSeparator}', siteNameSeparator)
          .replace('{pageTitle}', GLOBALS.currentPage.title || '');
      }

      metaDescription = t('casino.metaDescriptionMultiview')
        .replace('{gameDescriptionMain}', firstGame.data.description || '')
        .replace(
          '{gameDescriptionSecondary}',
          secondGame.data.description || ''
        )
        .replaceAll('{siteNameSeparator}', siteNameSeparator)
        .replaceAll('{siteName}', GLOBALS.appConfigs.siteName || '')
        .replace('{pageSeoDescription}', GLOBALS.currentPage.title || '');
    }

    if (document.title === metaTitle) {
      return;
    }

    generateMetaData(
      metaTitle,
      metaDescription,
      completeUrl,
      skipCanonicalAndOgUrl
    );
  }, [activeGames]);
};
